import type { WebsiteDefinition } from "@baze/schema";
import { bestOnColor, contrastRatio } from "./color";
import type { CompiledCatalog, Design, DesignTokens, Font } from "./types";

export interface ResolvedTheme {
  design: Design;
  tokens: DesignTokens;
  fonts: { heading: Font; body: Font };
  /** Warnings when user overrides reduce contrast (shown in the editor, never blocking render). */
  warnings: string[];
}

/**
 * Resolve the theme for a definition: design by id (+ version fallback to latest) with user overrides applied.
 * Returns null if the design does not exist in the catalog (callers render a safe fallback design).
 */
export function resolveTheme(catalog: Pick<CompiledCatalog, "designs" | "fonts" | "fontPairs">, theme: WebsiteDefinition["theme"]): ResolvedTheme | null {
  const candidates = catalog.designs.filter((d) => d.id === theme.id);
  const design = candidates.find((d) => d.version === theme.version) ?? candidates.sort((a, b) => b.version - a.version)[0];
  if (!design) return null;
  const warnings: string[] = [];
  const colors = { ...design.tokens.colors };
  const primary = theme.overrides?.primary;
  if (primary) {
    colors.primary = primary;
    colors.onPrimary = bestOnColor(primary);
    if (contrastRatio(colors.onPrimary, primary) < 4.5) warnings.push("Teks di atas tombol kurang kontras dengan warna utama.");
    if (contrastRatio(primary, colors.background) < 3) warnings.push("Warna utama kurang kontras dengan latar halaman.");
  }
  const pairId = theme.overrides?.fontPair ?? design.fontPair;
  const pair = catalog.fontPairs.find((p) => p.id === pairId) ?? catalog.fontPairs.find((p) => p.id === design.fontPair);
  const font = (id: string | undefined) => catalog.fonts.find((f) => f.id === id) ?? catalog.fonts[0]!;
  return { design, tokens: { ...design.tokens, colors }, fonts: { heading: font(pair?.heading), body: font(pair?.body) }, warnings };
}
